import React, { useEffect } from 'react';
import { Shield, Lock, FileText, Layers, Palette, Cpu, Tag, Globe, Award } from 'lucide-react';
import SEO from '../../components/SEO';
import LandingNavBar from '../../components/LandingNavBar';
import LandingFooter from '../../components/LandingFooter';

const heading = { fontFamily: "'Plus Jakarta Sans', 'Manrope', sans-serif" };

const IntellectualProperty = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const protectedItems = [
    { icon: Cpu, label: 'Software architecture & system logic' },
    { icon: Palette, label: 'User interface and experience (UI/UX)' },
    { icon: Layers, label: 'Workflow automation design' },
    { icon: FileText, label: 'Data structures and processes' },
    { icon: Tag, label: 'StayMii branding and platform identity' },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="ip-page">
      <SEO
        title="Intellectual Property | StayMii"
        description="StayMii Cloud Hospitality Management Software copyright and intellectual property information. Protected under the Copyright Act 1987 (Malaysia)."
        canonicalUrl="/intellectual-property"
      />
      <LandingNavBar />

      {/* Hero */}
      <div className="bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A] pt-32 pb-16 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-white/10 rounded-2xl mb-6">
            <Shield className="h-7 w-7 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4 tracking-tight" style={heading} data-testid="ip-title">
            Intellectual Property
          </h1>
          <p className="text-white/60 text-base md:text-lg max-w-xl mx-auto leading-relaxed">
            Proprietary technology owned and protected under Malaysian law.
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-16 md:py-20 space-y-16">

        {/* ── COPYRIGHT REGISTRATION ── */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
              <FileText className="w-5 h-5 text-[#2563EB]" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 tracking-tight" style={heading} data-testid="ip-copyright-heading">
              Copyright Registration
            </h2>
          </div>
          <p className="text-slate-600 leading-relaxed mb-8">
            StayMii&trade; Cloud Hospitality Management Software — covering the StayMii Lite property management, StayMii e-Invoice (LHDN MyInvois) module, and StayMii e-Ticket attraction &amp; event ticketing module — is a registered copyrighted work under Malaysia's Copyright Act 1987.
          </p>

          <div className="bg-slate-50 rounded-2xl border border-slate-200 overflow-hidden" data-testid="ip-details-table">
            <div className="divide-y divide-slate-200">
              {[
                ['Notification No', 'CRLY2026E01573'],
                ['Registered On', '19 March 2026'],
                ['Copyright Owner', 'Mii Global Partner Holdings Sdn Bhd (202601028203 (1690298-K))'],
                ['Registration Authority', 'MyIPO (Intellectual Property Corporation of Malaysia)'],
                ['Category of Work', 'Computer Program / Software (Literary Work)'],
                ['Country of Origin', 'Malaysia'],
              ].map(([k, v], i) => (
                <div key={i} className="flex flex-col sm:flex-row">
                  <div className="sm:w-[200px] shrink-0 px-6 py-4 bg-slate-100/80 font-semibold text-slate-700 text-sm">{k}</div>
                  <div className="flex-1 px-6 py-4 text-slate-600 text-sm">{v}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── TRADEMARK ── */}
        <section data-testid="ip-trademark-section">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center">
              <Award className="w-5 h-5 text-indigo-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 tracking-tight" style={heading} data-testid="ip-trademark-heading">
              Trademark
            </h2>
          </div>
          <p className="text-slate-600 leading-relaxed mb-6">
            StayMii&trade; is a trademark pending registration with the Intellectual Property Corporation of Malaysia (MyIPO). The StayMii&trade; name, logo, and associated branding are proprietary intellectual property. Unauthorized use, reproduction, imitation, or representation of the StayMii&trade; brand or logo without prior written permission may constitute an infringement of applicable trademark and intellectual property laws.
          </p>

          <div className="bg-indigo-50/50 rounded-2xl border border-indigo-100 overflow-hidden" data-testid="ip-trademark-table">
            <div className="divide-y divide-indigo-100">
              {[
                ['Mark', 'StayMii\u2122'],
                ['Status', 'Pending registration'],
                ['Registration Authority', 'MyIPO (Intellectual Property Corporation of Malaysia)'],
                ['Mark Owner', 'Mii Global Partner Holdings Sdn Bhd (202601028203 (1690298-K))'],
                ['Country of Filing', 'Malaysia'],
                ['Notice Symbol', '\u2122 (® is not used while registration is pending)'],
              ].map(([k, v], i) => (
                <div key={i} className="flex flex-col sm:flex-row">
                  <div className="sm:w-[200px] shrink-0 px-6 py-4 bg-indigo-100/50 font-semibold text-slate-700 text-sm">{k}</div>
                  <div className="flex-1 px-6 py-4 text-slate-600 text-sm">{v}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── WHAT'S PROTECTED ── */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center">
              <Shield className="w-5 h-5 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 tracking-tight" style={heading}>
              What's Protected
            </h2>
          </div>
          <p className="text-slate-600 leading-relaxed mb-8">
            The following elements of StayMii — across StayMii Lite, StayMii e-Invoice, and StayMii e-Ticket — are proprietary intellectual property:
          </p>

          <div className="grid sm:grid-cols-2 gap-4">
            {protectedItems.map((item, i) => (
              <div key={i} className="flex items-center gap-4 p-5 bg-slate-50 rounded-xl border border-slate-100">
                <div className="w-9 h-9 bg-white rounded-lg flex items-center justify-center border border-slate-200 shrink-0">
                  <item.icon className="w-4 h-4 text-slate-600" />
                </div>
                <span className="text-sm font-medium text-slate-700">{item.label}</span>
              </div>
            ))}
          </div>
        </section>

        {/* ── PROTECTION & USAGE GUIDELINES ── */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center">
              <Lock className="w-5 h-5 text-amber-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 tracking-tight" style={heading}>
              Protection & Usage Guidelines
            </h2>
          </div>
          <div className="space-y-4">
            <p className="text-slate-600 leading-relaxed">
              The StayMii&trade; software platform, including its source code, software architecture, databases, user interface, documentation, workflows, graphics, and related materials, is protected by copyright. The StayMii&trade; name, logo, and brand identity are protected under applicable trademark laws, with trademark registration currently pending before the Intellectual Property Corporation of Malaysia (MyIPO).
            </p>
            <p className="text-slate-600 leading-relaxed">
              StayMii is a proprietary platform. Any unauthorised reproduction, modification, or misuse of the system, design, or underlying technology is not permitted.
            </p>
            <p className="text-slate-600 leading-relaxed">
              All content, source code, architecture, algorithms, database schemas, user interface designs, and business logic embodied in the StayMii software — including StayMii Lite, StayMii e-Invoice, and StayMii e-Ticket — are the original intellectual property of Mii Global Partner Holdings Sdn Bhd.
            </p>
            <p className="text-slate-600 leading-relaxed">
              For licensing inquiries or intellectual property matters, please contact{' '}
              <a href="mailto:support@staymii.com" className="text-[#2563EB] hover:underline font-medium">support@staymii.com</a>.
            </p>
          </div>
        </section>

        {/* ── PORTFOLIO POSITIONING ── */}
        <div className="bg-slate-50 rounded-2xl border border-slate-200 p-8 text-center">
          <Globe className="w-6 h-6 text-[#2563EB] mx-auto mb-4" />
          <p className="text-slate-700 text-sm leading-relaxed font-medium max-w-lg mx-auto">
            StayMii is part of Mii Global Partner Holdings Sdn Bhd's proprietary technology portfolio, developed specifically for the hospitality, ticketing, and attraction industries.
          </p>
        </div>

        {/* ── TRADEMARK NOTICE ── */}
        <div className="border-t border-slate-200 pt-6" data-testid="ip-trademark-notice">
          <p className="text-xs text-slate-500 leading-relaxed text-center">
            <strong className="text-slate-600">Trademark Notice:</strong> StayMii&trade; is a trademark pending registration with the Intellectual Property Corporation of Malaysia (MyIPO).
          </p>
        </div>

      </div>

      <LandingFooter />
    </div>
  );
};

export default IntellectualProperty;
